import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  X,
  Share2,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import {
  resolvePortfolioBySlug,
  resolvePortfolioWorks,
  type PortfolioWork,
} from '../services/portfolio';
import { resolvePortfolioTheme } from '../data/portfolioThemes';
import type { PortfolioGrouping, PortfolioLayout, ProjectPhoto, ProjectStatus } from '../types';
import { cn } from '../utils/formatters';

const STATUS_LABEL: Record<ProjectStatus, string> = {
  planned: 'Planificado',
  in_progress: 'En curso',
  stopped: 'Parado',
  finished: 'Finalizado',
};

export function PublicPortfolioPage() {
  const { slug } = useParams();
  const resolved = useMemo(() => (slug ? resolvePortfolioBySlug(slug) : null), [slug]);

  const [openWork, setOpenWork] = useState<PortfolioWork | null>(null);
  const [box, setBox] = useState<{ photos: ProjectPhoto[]; index: number } | null>(null);
  const [activeYear, setActiveYear] = useState<string>('all');

  const { preset, vars, hero, layout, grouping } = resolvePortfolioTheme(resolved?.portfolio ?? {});

  useEffect(() => {
    if (resolved) document.title = `${resolved.portfolio.title} · Portfolio`;
    return () => {
      document.title = 'ReformAll';
    };
  }, [resolved]);

  useEffect(() => {
    if (!box && !openWork) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (box) setBox(null);
        else setOpenWork(null);
      }
      if (box && e.key === 'ArrowRight') setBox(b => (b ? { ...b, index: (b.index + 1) % b.photos.length } : b));
      if (box && e.key === 'ArrowLeft') setBox(b => (b ? { ...b, index: (b.index - 1 + b.photos.length) % b.photos.length } : b));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [box, openWork]);

  if (!resolved) {
    return (
      <PortfolioMessage
        title="Portfolio no encontrado"
        message="El enlace que has abierto no corresponde a ningún portfolio activo."
      />
    );
  }

  if (!resolved.portfolio.enabled) {
    return (
      <PortfolioMessage
        title="Portfolio no disponible"
        message="Esta empresa todavía no ha publicado su portfolio público."
      />
    );
  }

  const { company, portfolio } = resolved;
  const works = resolvePortfolioWorks(company.id, portfolio).filter(w => w.photos.length > 0);
  const cover =
    portfolio.coverImage || works.flatMap(w => w.photos).find(p => p.url)?.url;

  const years = Array.from(new Set(workYears(works))).sort((a, b) => b - a);
  const visible =
    grouping === 'none' && activeYear !== 'all'
      ? works.filter(w => String(yearOf(w.project.startDate) ?? '') === activeYear)
      : works;
  const groups = buildGroups(visible, grouping);

  const whatsappHref = portfolio.contactWhatsapp
    ? `https://wa.me/${portfolio.contactWhatsapp.replace(/[^0-9]/g, '')}`
    : undefined;

  return (
    <div className="pf-root" style={vars as CSSProperties}>
      {hero === 'overlay' && (
        <header className="relative h-64 sm:h-80 lg:h-96 w-full overflow-hidden">
          {cover ? (
            <img src={cover} alt={portfolio.title} className="w-full h-full object-cover" />
          ) : (
            <div className="pf-hero-fallback w-full h-full" />
          )}
          <div className="pf-hero-overlay__scrim absolute inset-0" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 pb-8">
              <div className="flex items-center gap-4">
                <CompanyMark company={company} onDark />
                <div>
                  <h1 className="pf-heading pf-hero-text text-3xl sm:text-4xl font-extrabold text-white">
                    {portfolio.title}
                  </h1>
                  {portfolio.subtitle && (
                    <p className="pf-hero-text text-white/90 mt-1">{portfolio.subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {hero === 'banner' && (
        <header className="pf-soft border-b pf-divider">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center flex flex-col items-center">
            <CompanyMark company={company} />
            <h1 className="pf-heading pf-accent text-3xl sm:text-4xl font-extrabold mt-4">
              {portfolio.title}
            </h1>
            {portfolio.subtitle && <p className="pf-muted mt-2 text-lg">{portfolio.subtitle}</p>}
          </div>
          {cover && (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
              <img
                src={cover}
                alt={portfolio.title}
                className="w-full h-56 sm:h-72 lg:h-80 object-cover rounded-[var(--pf-radius)] border pf-divider"
              />
            </div>
          )}
        </header>
      )}

      {hero === 'minimal' && (
        <header className="border-b pf-divider">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
            <div className="flex items-center gap-4">
              <CompanyMark company={company} />
              <div>
                <h1 className="pf-heading text-2xl sm:text-3xl font-extrabold">{portfolio.title}</h1>
                {portfolio.subtitle && <p className="pf-muted mt-1">{portfolio.subtitle}</p>}
              </div>
            </div>
            {cover && (
              <img
                src={cover}
                alt={portfolio.title}
                className="w-full h-52 sm:h-72 lg:h-80 object-cover rounded-[var(--pf-radius)] border pf-divider mt-8"
              />
            )}
          </div>
        </header>
      )}

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-12">
        {portfolio.description && (
          <section className="pf-about">
            <span className="pf-about__rule" />
            <p className="pf-about__text">{portfolio.description}</p>
          </section>
        )}

        <section className="space-y-8">
          {grouping === 'none' && years.length > 1 && layout !== 'showcase' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="pf-chip"
                aria-pressed={activeYear === 'all'}
                onClick={() => setActiveYear('all')}
              >
                Todas
              </button>
              {years.map(y => (
                <button
                  key={y}
                  type="button"
                  className="pf-chip"
                  aria-pressed={activeYear === String(y)}
                  onClick={() => setActiveYear(String(y))}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {visible.length === 0 ? (
            <p className="pf-muted py-10 text-center">Todavía no hay obras publicadas.</p>
          ) : (
            groups.map(group => (
              <div key={group.key} className="space-y-6">
                {group.label && (
                  <h2 className="pf-heading text-2xl font-bold flex items-center gap-3">
                    {group.label}
                    <span className="pf-divider border-t flex-1" />
                  </h2>
                )}
                <WorksGrid
                  works={group.works}
                  layout={layout}
                  onOpenWork={setOpenWork}
                  onOpenPhoto={(photos, index) => setBox({ photos, index })}
                />
              </div>
            ))
          )}
        </section>

        <section className="pf-surface p-6 sm:p-8">
          <h2 className="pf-heading text-2xl font-bold mb-2">¿Hablamos de tu reforma?</h2>
          <p className="pf-muted mb-6">
            Pide presupuesto sin compromiso. Estaremos encantados de asesorarte.
          </p>
          <div className="flex flex-wrap gap-3">
            {portfolio.contactPhone && (
              <a href={`tel:${portfolio.contactPhone.replace(/\s/g, '')}`} className="pf-btn">
                <Phone className="w-4 h-4" /> Llamar
              </a>
            )}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="pf-btn">
                <WaIcon /> WhatsApp
              </a>
            )}
            {portfolio.contactEmail && (
              <a href={`mailto:${portfolio.contactEmail}`} className="pf-btn pf-btn--ghost">
                <Mail className="w-4 h-4" /> Email
              </a>
            )}
          </div>
          {(company.address || company.phone || company.email) && (
            <div className="mt-6 pt-6 border-t pf-divider text-sm pf-muted space-y-1">
              {company.address && (
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {company.address}
                </p>
              )}
              {company.phone && (
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {company.phone}
                </p>
              )}
              {company.email && (
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {company.email}
                </p>
              )}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t pf-divider py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-sm pf-muted">
          Portfolio de {company.name} · Hecho con ReformAll
        </div>
      </footer>

      {openWork && (
        <WorkModal
          work={openWork}
          onClose={() => setOpenWork(null)}
          onOpenPhoto={(photos, index) => setBox({ photos, index })}
        />
      )}

      {box && (
        <Lightbox
          photos={box.photos}
          index={box.index}
          onChange={i => setBox({ photos: box.photos, index: i })}
          onClose={() => setBox(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function CompanyMark({
  company,
  onDark = false,
}: {
  company: { name: string; logo?: string };
  onDark?: boolean;
}) {
  const base = 'w-14 h-14 rounded-xl object-cover shrink-0';
  if (company.logo) {
    return (
      <img
        src={company.logo}
        alt={company.name}
        className={cn(base, onDark ? 'bg-white/90 p-1 shadow-lg' : 'border pf-divider bg-[var(--pf-surface)]')}
      />
    );
  }
  return (
    <div className={cn(base, 'flex items-center justify-center text-2xl font-bold bg-[var(--pf-accent)] text-[var(--pf-accent-contrast)]')}>
      {company.name.charAt(0)}
    </div>
  );
}

const MASONRY_ASPECT = ['aspect-[3/4]', 'aspect-[1/1]', 'aspect-[4/5]', 'aspect-[4/3]', 'aspect-[5/6]'];

function WorksGrid({
  works,
  layout,
  onOpenWork,
  onOpenPhoto,
}: {
  works: PortfolioWork[];
  layout: PortfolioLayout;
  onOpenWork: (w: PortfolioWork) => void;
  onOpenPhoto: (photos: ProjectPhoto[], index: number) => void;
}) {
  if (layout === 'showcase') {
    return (
      <div className="space-y-12">
        {works.map(work => (
          <ShowcaseWork key={work.project.id} work={work} onOpenPhoto={onOpenPhoto} />
        ))}
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div className="space-y-3">
        {works.map(work => (
          <WorkRow key={work.project.id} work={work} onOpen={onOpenWork} />
        ))}
      </div>
    );
  }

  if (layout === 'featured' && works.length > 0) {
    const [first, ...rest] = works;
    return (
      <div className="space-y-6">
        <FeaturedCard work={first} onOpen={onOpenWork} />
        {rest.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map(work => (
              <WorkCard key={work.project.id} work={work} onOpen={onOpenWork} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (layout === 'masonry') {
    return (
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
        {works.map((work, i) => (
          <div key={work.project.id} className="mb-5 break-inside-avoid">
            <WorkCard work={work} onOpen={onOpenWork} heightClass={MASONRY_ASPECT[i % MASONRY_ASPECT.length]} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {works.map(work => (
        <WorkCard key={work.project.id} work={work} onOpen={onOpenWork} />
      ))}
    </div>
  );
}

function WorkCard({
  work,
  onOpen,
  heightClass = 'aspect-[4/3]',
}: {
  work: PortfolioWork;
  onOpen: (w: PortfolioWork) => void;
  heightClass?: string;
}) {
  const cover = coverPhoto(work);
  const year = yearOf(work.project.startDate);
  const city = cityFromAddress(work.project.address);

  return (
    <button type="button" onClick={() => onOpen(work)} className="pf-card group">
      <div className={cn('relative overflow-hidden', heightClass)}>
        {cover ? (
          <img
            src={cover.thumbnailUrl || cover.url}
            alt={work.project.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="pf-soft w-full h-full" />
        )}
        {work.photos.length > 1 && (
          <span className="pf-tag absolute top-3 right-3">{work.photos.length} fotos</span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          {year && (
            <span className="pf-tag">
              <Calendar className="w-3 h-3" /> {year}
            </span>
          )}
          <span className="text-xs pf-muted">{STATUS_LABEL[work.project.status]}</span>
        </div>
        <h3 className="pf-heading text-lg font-semibold leading-tight">{work.project.name}</h3>
        {city && (
          <p className="pf-muted text-sm mt-1 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {city}
          </p>
        )}
      </div>
    </button>
  );
}

function FeaturedCard({ work, onOpen }: { work: PortfolioWork; onOpen: (w: PortfolioWork) => void }) {
  const cover = coverPhoto(work);
  const year = yearOf(work.project.startDate);
  const city = cityFromAddress(work.project.address);

  return (
    <button type="button" onClick={() => onOpen(work)} className="pf-card group">
      <div className="relative aspect-[16/10] sm:aspect-[21/9] overflow-hidden">
        {cover ? (
          <img src={cover.thumbnailUrl || cover.url} alt={work.project.name} className="w-full h-full object-cover" />
        ) : (
          <div className="pf-hero-fallback w-full h-full" />
        )}
        <div className="pf-hero-overlay__scrim absolute inset-0" />
        {work.photos.length > 1 && (
          <span className="pf-tag absolute top-3 right-3">{work.photos.length} fotos</span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
          <div className="flex items-center gap-2 mb-2 text-xs">
            {year && <span className="pf-tag">{year}</span>}
            <span className="pf-hero-text text-white/80">{STATUS_LABEL[work.project.status]}</span>
          </div>
          <h3 className="pf-heading pf-hero-text text-2xl sm:text-3xl font-bold">{work.project.name}</h3>
          {city && (
            <p className="pf-hero-text text-white/85 text-sm mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {city}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function WorkRow({ work, onOpen }: { work: PortfolioWork; onOpen: (w: PortfolioWork) => void }) {
  const cover = coverPhoto(work);
  const year = yearOf(work.project.startDate);
  const city = cityFromAddress(work.project.address);

  return (
    <button type="button" onClick={() => onOpen(work)} className="pf-row group">
      <div className="relative w-28 sm:w-40 shrink-0 overflow-hidden">
        {cover ? (
          <img
            src={cover.thumbnailUrl || cover.url}
            alt={work.project.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="pf-soft w-full h-full min-h-[104px]" />
        )}
      </div>
      <div className="flex-1 min-w-0 p-4 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-1">
          {year && (
            <span className="pf-tag">
              <Calendar className="w-3 h-3" /> {year}
            </span>
          )}
          <span className="text-xs pf-muted">{STATUS_LABEL[work.project.status]}</span>
        </div>
        <h3 className="pf-heading text-base sm:text-lg font-semibold leading-tight">{work.project.name}</h3>
        {city && (
          <p className="pf-muted text-sm mt-1 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {city}
          </p>
        )}
        {work.photos.length > 0 && <p className="pf-muted text-xs mt-1">{work.photos.length} fotos</p>}
      </div>
      <div className="hidden sm:flex items-center pr-5 pf-muted">
        <ChevronRight className="w-5 h-5" />
      </div>
    </button>
  );
}

function ShowcaseWork({
  work,
  onOpenPhoto,
}: {
  work: PortfolioWork;
  onOpenPhoto: (photos: ProjectPhoto[], index: number) => void;
}) {
  const year = yearOf(work.project.startDate);
  return (
    <article className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="pf-heading text-2xl font-bold">{work.project.name}</h3>
        {year && (
          <span className="pf-tag">
            <Calendar className="w-3 h-3" /> {year}
          </span>
        )}
        {work.project.address && (
          <span className="pf-muted text-sm flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {work.project.address}
          </span>
        )}
      </div>
      {work.project.description && <p className="pf-muted max-w-3xl">{work.project.description}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {work.photos.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => onOpenPhoto(work.photos, i)}
            className="group relative aspect-[4/3] overflow-hidden rounded-[var(--pf-radius)] border pf-divider"
          >
            <img
              src={photo.thumbnailUrl || photo.url}
              alt={photo.caption || work.project.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {photo.caption && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                {photo.caption}
              </span>
            )}
          </button>
        ))}
      </div>
    </article>
  );
}

function WorkModal({
  work,
  onClose,
  onOpenPhoto,
}: {
  work: PortfolioWork;
  onClose: () => void;
  onOpenPhoto: (photos: ProjectPhoto[], index: number) => void;
}) {
  const year = yearOf(work.project.startDate);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" onClick={onClose}>
      <div
        className="pf-surface w-full max-w-3xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-[var(--pf-radius)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 p-5 border-b pf-divider bg-[var(--pf-surface)]">
          <div>
            <h2 className="pf-heading text-xl font-bold">{work.project.name}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm pf-muted">
              {year && <span>{year}</span>}
              {work.project.address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {work.project.address}
                </span>
              )}
              <span>{STATUS_LABEL[work.project.status]}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="pf-btn pf-btn--ghost !p-2" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>
        {work.project.description && (
          <p className="pf-muted px-5 pt-4">{work.project.description}</p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-5">
          {work.photos.map((photo, i) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onOpenPhoto(work.photos, i)}
              className="group relative aspect-[4/3] overflow-hidden rounded-[var(--pf-radius)] border pf-divider"
            >
              <img
                src={photo.thumbnailUrl || photo.url}
                alt={photo.caption || work.project.name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Lightbox({
  photos,
  index,
  onChange,
  onClose,
}: {
  photos: ProjectPhoto[];
  index: number;
  onChange: (i: number) => void;
  onClose: () => void;
}) {
  const photo = photos[index];
  if (!photo) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4" onClick={onClose}>
      <button className="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white/20" aria-label="Cerrar">
        <X className="w-6 h-6" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-3 p-3 rounded-full text-white hover:bg-white/20"
            aria-label="Anterior"
            onClick={e => {
              e.stopPropagation();
              onChange((index - 1 + photos.length) % photos.length);
            }}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            className="absolute right-3 p-3 rounded-full text-white hover:bg-white/20"
            aria-label="Siguiente"
            onClick={e => {
              e.stopPropagation();
              onChange((index + 1) % photos.length);
            }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}
      <figure className="max-h-[85vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
        <img src={photo.url} alt={photo.caption || 'Trabajo'} className="max-h-[78vh] max-w-full object-contain rounded-lg" />
        {photo.caption && <figcaption className="text-white/80 text-sm">{photo.caption}</figcaption>}
      </figure>
    </div>
  );
}

function PortfolioMessage({ title, message }: { title: string; message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-950 p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center">
          <Share2 className="w-8 h-8 text-secondary-400" />
        </div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">{title}</h1>
        <p className="text-secondary-500">{message}</p>
      </div>
    </div>
  );
}

/* --------------------------------- helpers -------------------------------- */

function coverPhoto(work: PortfolioWork): ProjectPhoto | undefined {
  return work.photos.find(p => p.phase === 'after' && (p.thumbnailUrl || p.url)) ?? work.photos[0];
}

function yearOf(date?: string): number | null {
  if (!date) return null;
  const y = new Date(date).getFullYear();
  return Number.isNaN(y) ? null : y;
}

function workYears(works: PortfolioWork[]): number[] {
  return works
    .map(w => yearOf(w.project.startDate))
    .filter((y): y is number => y !== null);
}

/** Deriva la localidad del campo `address` (p. ej. "C/ Mayor 45, 28013 Madrid" → "Madrid"). */
function cityFromAddress(address?: string): string {
  if (!address) return '';
  const parts = address
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  const last = parts[parts.length - 1] ?? '';
  return last.replace(/^\d{4,5}\s*/, '').trim() || address.trim();
}

/** Reglas para inferir el tipo de reforma a partir del nombre y la descripción. */
const REFORM_TYPE_RULES: { label: string; keywords: string[] }[] = [
  { label: 'Baño', keywords: ['baño', 'bano', 'aseo', 'sanitario'] },
  { label: 'Cocina', keywords: ['cocina'] },
  { label: 'Salón', keywords: ['salón', 'salon'] },
  { label: 'Fachada', keywords: ['fachada'] },
  { label: 'Local comercial', keywords: ['local', 'comercial', 'panadería', 'panaderia', 'tienda', 'oficina'] },
  { label: 'Vivienda completa', keywords: ['piso', 'vivienda', 'casa', 'chalet', 'ático', 'atico'] },
  { label: 'Pintura', keywords: ['pintura', 'pintar', 'pintado'] },
  { label: 'Pladur y tabiques', keywords: ['pladur', 'tabique'] },
  { label: 'Reforma integral', keywords: ['integral'] },
];

function reformType(work: PortfolioWork): string {
  const text = `${work.project.name} ${work.project.description ?? ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const hit = REFORM_TYPE_RULES.find(rule =>
    rule.keywords.some(kw => text.includes(kw.normalize('NFD').replace(/[\u0300-\u036f]/g, '')))
  );
  return hit?.label ?? 'Otros';
}

function groupLabelFor(work: PortfolioWork, grouping: PortfolioGrouping): string {
  switch (grouping) {
    case 'year':
      return String(yearOf(work.project.startDate) ?? 'Sin año');
    case 'location':
      return cityFromAddress(work.project.address) || 'Otras localidades';
    case 'type':
      return reformType(work);
    case 'status':
      return STATUS_LABEL[work.project.status];
    case 'client':
      return work.clientName || 'Sin cliente';
    default:
      return '';
  }
}

const STATUS_ORDER: ProjectStatus[] = ['in_progress', 'planned', 'stopped', 'finished'];

function buildGroups(
  works: PortfolioWork[],
  grouping: PortfolioGrouping
): { key: string; label: string; works: PortfolioWork[] }[] {
  if (grouping === 'none') return [{ key: 'all', label: '', works }];

  const map = new Map<string, PortfolioWork[]>();
  for (const work of works) {
    const label = groupLabelFor(work, grouping);
    const list = map.get(label) ?? [];
    list.push(work);
    map.set(label, list);
  }

  return Array.from(map.entries())
    .sort((a, b) => {
      if (grouping === 'year') {
        const na = Number(a[0]);
        const nb = Number(b[0]);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
      }
      if (grouping === 'status') {
        return STATUS_ORDER.indexOf(statusFromLabel(a[0])) - STATUS_ORDER.indexOf(statusFromLabel(b[0]));
      }
      return a[0].localeCompare(b[0], 'es');
    })
    .map(([label, groupWorks]) => ({ key: label, label, works: groupWorks }));
}

function statusFromLabel(label: string): ProjectStatus {
  const entry = (Object.entries(STATUS_LABEL) as [ProjectStatus, string][]).find(([, v]) => v === label);
  return entry?.[0] ?? 'planned';
}

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.06c-.24.68-1.42 1.3-1.96 1.38-.5.08-1.13.11-1.82-.11-.42-.13-.96-.31-1.65-.61-2.9-1.25-4.8-4.17-4.94-4.36-.15-.19-1.19-1.58-1.19-3s.75-2.13 1.02-2.42c.26-.29.57-.36.77-.36s.39 0 .56.01c.18.01.42-.07.66.5.24.58.82 2 .89 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.56.15.27.68 1.12 1.47 1.82 1.01.9 1.86 1.18 2.13 1.32.27.14.42.12.58-.07.15-.19.67-.78.85-1.05.18-.27.36-.22.6-.13.24.09 1.55.73 1.82.86.27.14.45.2.51.31.07.11.07.64-.17 1.32Z" />
    </svg>
  );
}
